import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import STLViewer from '../components/3d/STLViewer';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../hooks/useNotification';
import {
  uploadPhoto,
  createMemory,
  updateMemory,
  updateMemory3DModel,
  updateMemoryStatus,
  generateFigurine,
  deleteMemory,
  addToCart,
  generate3DModelFromApi,
  checkMemoryFigurineStatus,
  checkMemory3DStatus,
  checkMemoryStatus,
  getRemainingFigurineGenerations,
  getDisplayUrl,
  extractFilePathFromUrl,
  getProductPrice,
} from '../services/memoryService';
import logger from '../utils/logger';
import './CreateMemories.css';
import Navbar from '../components/layout/Navbar';
import Button from '../components/common/Button';

function Create() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [memoryTitle, setMemoryTitle] = useState('');
  const [memoryDescription, setMemoryDescription] = useState('');
  // removed isUploading which was unused
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedImageDisplayUrl, setGeneratedImageDisplayUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generated3DModel, setGenerated3DModel] = useState(null);
  const [generated3DModelDisplayUrl, setGenerated3DModelDisplayUrl] = useState(null);
  const [isGenerating3D, setIsGenerating3D] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [savedMemory, setSavedMemory] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [existingMemory, setExistingMemory] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [existingPhotoDisplayUrl, setExistingPhotoDisplayUrl] = useState(null);
  const [hasClickedGenerate3D, setHasClickedGenerate3D] = useState(false);
  const [viewerDimensions, setViewerDimensions] = useState({ width: 350, height: 350 });
  const figurinePollRef = useRef(null);
  const modelPollRef = useRef(null);
  const fileInputRef = useRef(null);
  const titleUpdateTimeoutRef = useRef(null);
  // Loading bar state for 2D generation
  const [generationProgress, setGenerationProgress] = useState(0);
  const [imageReady, setImageReady] = useState(false);
  const [imageRevealReady, setImageRevealReady] = useState(false);
  const progressTimerRef = useRef(null);
  const progressStartTimeRef = useRef(0);
  const imageReadyRef = useRef(false);
  // Loading bar state for 3D generation
  const [generation3DProgress, setGeneration3DProgress] = useState(0);
  const [modelReady, setModelReady] = useState(false);
  const [modelRevealReady, setModelRevealReady] = useState(false);
  const progress3DTimerRef = useRef(null);
  const progress3DStartTimeRef = useRef(0);
  const modelReadyRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showDeleteConfirm, showSuccess, showError } = useNotification();

  const [remainingGenerations, setRemainingGenerations] = useState(null);
  const [dailyLimit, setDailyLimit] = useState(null);
  const [productPrice, setProductPrice] = useState(25); // Default fallback price
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [figurineImages, setFigurineImages] = useState([]);
  // Touch swipe refs for mobile carousel
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchEndXRef = useRef(0);
  const isSwipingRef = useRef(false);
  const figurineUrlUpdateTimeoutRef = useRef(null);

  // Keep ref in sync
  useEffect(() => {
    imageReadyRef.current = imageReady;
  }, [imageReady]);

  // Keep 3D ready ref in sync
  useEffect(() => {
    modelReadyRef.current = modelReady;
  }, [modelReady]);

  // Manage 120s progress bar that pauses at 97% until image is ready
  useEffect(() => {
    const TARGET_DURATION_MS = 120000;
    const PAUSE_PERCENT = 97;

    if (isGenerating) {
      // reset and start timer
      setGenerationProgress(0);
      setImageRevealReady(false);
      setImageReady(false);
      progressStartTimeRef.current = Date.now();
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
      progressTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - progressStartTimeRef.current;
        const intended = Math.floor((elapsed / TARGET_DURATION_MS) * 100);
        const next = Math.min(PAUSE_PERCENT, intended);
        setGenerationProgress((prev) => (prev < PAUSE_PERCENT ? Math.min(PAUSE_PERCENT, next) : prev));

        const reachedTime = elapsed >= TARGET_DURATION_MS;
        if (reachedTime && imageReadyRef.current) {
          // finalize
          setGenerationProgress(100);
          setImageRevealReady(true);
          setIsGenerating(false);
          if (progressTimerRef.current) {
            clearInterval(progressTimerRef.current);
            progressTimerRef.current = null;
          }
        }
      }, 100);
    } else {
      // stop timer if not generating
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    }

    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, [isGenerating]);

  // If image becomes ready, finish immediately (regardless of elapsed time)
  useEffect(() => {
    if (!isGenerating) return;
    if (imageReady) {
      setGenerationProgress(100);
      setImageRevealReady(true);
      setIsGenerating(false);
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    }
  }, [imageReady, isGenerating]);

  // Manage 90s progress bar for 3D that pauses at 97% until model is ready
  useEffect(() => {
    const TARGET_DURATION_MS = 90000;
    const PAUSE_PERCENT = 97;

    if (isGenerating3D) {
      setGeneration3DProgress(0);
      setModelRevealReady(false);
      setModelReady(false);
      progress3DStartTimeRef.current = Date.now();
      if (progress3DTimerRef.current) {
        clearInterval(progress3DTimerRef.current);
      }
      progress3DTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - progress3DStartTimeRef.current;
        const intended = Math.floor((elapsed / TARGET_DURATION_MS) * 100);
        const next = Math.min(PAUSE_PERCENT, intended);
        setGeneration3DProgress((prev) => (prev < PAUSE_PERCENT ? Math.min(PAUSE_PERCENT, next) : prev));

        const reachedTime = elapsed >= TARGET_DURATION_MS;
        if (reachedTime && modelReadyRef.current) {
          setGeneration3DProgress(100);
          setModelRevealReady(true);
          setIsGenerating3D(false);
          if (progress3DTimerRef.current) {
            clearInterval(progress3DTimerRef.current);
            progress3DTimerRef.current = null;
          }
        }
      }, 100);
    } else {
      if (progress3DTimerRef.current) {
        clearInterval(progress3DTimerRef.current);
        progress3DTimerRef.current = null;
      }
    }

    return () => {
      if (progress3DTimerRef.current) {
        clearInterval(progress3DTimerRef.current);
        progress3DTimerRef.current = null;
      }
    };
  }, [isGenerating3D]);

  // If model becomes ready, finish immediately (regardless of elapsed time)
  useEffect(() => {
    if (!isGenerating3D) return;
    if (modelReady) {
      setGeneration3DProgress(100);
      setModelRevealReady(true);
      setIsGenerating3D(false);
      if (progress3DTimerRef.current) {
        clearInterval(progress3DTimerRef.current);
        progress3DTimerRef.current = null;
      }
    }
  }, [modelReady, isGenerating3D]);

  // Helper function to update display URL for generated image
  const updateGeneratedImageDisplayUrl = async (imageUrl, options) => {
    if (!imageUrl) {
      setGeneratedImageDisplayUrl(null);
      return;
    }
    
    try {
      const displayUrl = await getDisplayUrl(imageUrl, options);
      setGeneratedImageDisplayUrl(displayUrl);
    } catch (error) {
      logger.error('Error getting display URL for generated image:', error);
      setGeneratedImageDisplayUrl(imageUrl); // Fallback to original URL
    }
  };

  // Helper function to update display URL for generated 3D model
  const updateGenerated3DModelDisplayUrl = async (modelUrl) => {
    if (!modelUrl) {
      setGenerated3DModelDisplayUrl(null);
      return;
    }

    try {
      const displayUrl = await getDisplayUrl(modelUrl);
      setGenerated3DModelDisplayUrl(displayUrl);
    } catch (error) {
      logger.error('Error getting display URL for generated 3D model:', error);
      setGenerated3DModelDisplayUrl(modelUrl); // Fallback to original URL
    }
  };

  const detect3DModelFormat = (url) => {
    if (!url || typeof url !== 'string') {
      return 'STL';
    }

    try {
      const normalized = url.split('?')[0].split('#')[0].trim().toLowerCase();
      if (normalized.endsWith('.obj')) return 'OBJ';
      if (normalized.endsWith('.stl')) return 'STL';
    } catch (error) {
      logger.warn('Failed to detect 3D model format, defaulting to STL:', error);
    }

    return 'STL';
  };

  // Helper function to get current figurine image
  const getCurrentFigurineImage = () => {
    if (figurineImages.length === 0) {
      return generatedImage;
    }
    return figurineImages[currentImageIndex] || generatedImage;
  };

  // Helper function to get current figurine image display URL
  const getCurrentFigurineImageDisplayUrl = () => {
    if (figurineImages.length === 0) {
      return generatedImageDisplayUrl;
    }
    return figurineImages[currentImageIndex]?.displayUrl || generatedImageDisplayUrl;
  };

  // Carousel navigation handlers
  const goToPrevImage = () => {
    if (figurineImages.length <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + figurineImages.length) % figurineImages.length);
  };

  const goToNextImage = () => {
    if (figurineImages.length <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % figurineImages.length);
  };

  // Swipe gesture handlers (mobile)
  const handleFigurineTouchStart = (e) => {
    if (figurineImages.length <= 1) return;
    if (e.touches && e.touches.length === 1) {
      const touch = e.touches[0];
      touchStartXRef.current = touch.clientX;
      touchStartYRef.current = touch.clientY;
      touchEndXRef.current = touch.clientX;
      isSwipingRef.current = true;
    }
  };

  const handleFigurineTouchMove = (e) => {
    if (!isSwipingRef.current) return;
    if (!e.touches || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;
    // If vertical movement dominates, treat as scroll and stop swiping
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      isSwipingRef.current = false;
      return;
    }
    // Horizontal gesture: prevent native horizontal scrolling for smoother swipe
    e.preventDefault();
    touchEndXRef.current = touch.clientX;
  };

  const handleFigurineTouchEnd = () => {
    if (!isSwipingRef.current) return;
    const deltaX = (touchEndXRef.current || touchStartXRef.current) - touchStartXRef.current;
    const SWIPE_THRESHOLD = 50; // pixels
    if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
      if (deltaX < 0) {
        // swipe left → next image
        goToNextImage();
      } else {
        // swipe right → previous image
        goToPrevImage();
      }
    }
    isSwipingRef.current = false;
    touchStartXRef.current = 0;
    touchStartYRef.current = 0;
    touchEndXRef.current = 0;
  };

  // Persist the currently focused figurine image URL as figurine_url in DB (debounced)
  useEffect(() => {
    const memoryId = savedMemory?.id || existingMemory?.id;
    if (!memoryId || !user) return;

    // Determine the focal image URL to persist
    let selectedUrl = null;
    if (figurineImages && figurineImages.length > 0) {
      selectedUrl = figurineImages[currentImageIndex]?.url || null;
    } else if (generatedImage?.url) {
      selectedUrl = generatedImage.url;
    }

    if (!selectedUrl) return;

    // Skip if already set to this URL locally
    const currentPersisted = savedMemory?.figurine_url || existingMemory?.figurine_url;
    if (currentPersisted === selectedUrl) return;

    // Debounce updates to avoid spamming while swiping
    if (figurineUrlUpdateTimeoutRef.current) {
      clearTimeout(figurineUrlUpdateTimeoutRef.current);
    }

    figurineUrlUpdateTimeoutRef.current = setTimeout(async () => {
      try {
        await updateMemory(memoryId, { figurine_url: selectedUrl });
        // Keep local state in sync
        if (savedMemory) {
          setSavedMemory((prev) => (prev ? { ...prev, figurine_url: selectedUrl } : prev));
        }
        if (existingMemory) {
          setExistingMemory((prev) => (prev ? { ...prev, figurine_url: selectedUrl } : prev));
        }
      } catch (e) {
        logger.error('Failed to persist focal figurine_url:', e);
      }
    }, 500);

    return () => {
      if (figurineUrlUpdateTimeoutRef.current) {
        clearTimeout(figurineUrlUpdateTimeoutRef.current);
        figurineUrlUpdateTimeoutRef.current = null;
      }
    };
  }, [currentImageIndex, figurineImages, generatedImage?.url, savedMemory?.id, existingMemory?.id, user]);

  // Manage preview URL lifecycle when selectedFile changes
  useEffect(() => {
    let urlToRevoke = null;
    if (selectedFile && selectedFile.name !== 'existing-photo.jpg') {
      const newUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(newUrl);
      urlToRevoke = newUrl;
      logger.debug('Created object URL from selected file:', newUrl);
    } else {
      setPreviewUrl(null);
    }
    return () => {
      if (urlToRevoke) {
        URL.revokeObjectURL(urlToRevoke);
      }
    };
  }, [selectedFile]);

  // Resolve a display URL for existing photo paths when editing
  useEffect(() => {
    const resolveDisplay = async () => {
      try {
        if (selectedFile && selectedFile.name === 'existing-photo.jpg' && existingMemory?.photo_url) {
          const display = await getDisplayUrl(existingMemory.photo_url, {
            transform: { width: 1200, quality: 80, resize: 'contain' },
          });
          setExistingPhotoDisplayUrl(display);
        } else {
          setExistingPhotoDisplayUrl(null);
        }
      } catch (e) {
        logger.error('Failed to resolve existing photo display URL:', e);
        setExistingPhotoDisplayUrl(null);
      }
    };
    resolveDisplay();
  }, [selectedFile, existingMemory?.photo_url]);

  // Polling function to check for figurine_url availability
  const startPollingForFigurine = (memoryId) => {
    if (figurinePollRef.current) {
      return; // Already polling
    }

    const interval = setInterval(async () => {
      try {
        const memoryData = await checkMemoryFigurineStatus(memoryId);
        if (memoryData.status === 'completed_cartoon') {
          clearInterval(interval);
          figurinePollRef.current = null;

          const imageObj = {
            url: memoryData.figurine_url,
            alt: `Generated figurine for ${memoryTitle}`,
          };
          setGeneratedImage(imageObj);
          updateGeneratedImageDisplayUrl(memoryData.figurine_url, {
            transform: { width: 1200, quality: 80, resize: 'contain' },
          });
          // Keep local memory references in sync so downstream UI stays consistent
          setSavedMemory((prev) =>
            prev
              ? {
                  ...prev,
                  figurine_url: memoryData.figurine_url,
                  figurine_urls: memoryData.figurine_urls || prev.figurine_urls,
                }
              : prev
          );
          setExistingMemory((prev) =>
            prev
              ? {
                  ...prev,
                  figurine_url: memoryData.figurine_url,
                  figurine_urls: memoryData.figurine_urls || prev.figurine_urls,
                }
              : prev
          );
          setImageReady(true);
          // Instantly reveal image and stop generating state so UI updates without navigation
          setImageRevealReady(true);
          setIsGenerating(false);
          setUploadError('');
        }
      } catch (error) {
        logger.error('Error during figurine polling:', error);
      }
    }, 5000); // Poll every 5 seconds

    figurinePollRef.current = interval;
  };

  const stopPollingFigurine = () => {
    if (figurinePollRef.current) {
      clearInterval(figurinePollRef.current);
      figurinePollRef.current = null;
    }
  };

  // Polling function to check for model_3d_url availability
  const startPollingFor3DModel = (memoryId) => {
    if (modelPollRef.current) {
      return; // Already polling
    }

    const interval = setInterval(async () => {
      try {
        const memoryData = await checkMemory3DStatus(memoryId);
        if (memoryData.model_3d_url) {
          clearInterval(interval);
          modelPollRef.current = null;

          const model = {
            modelUrl: memoryData.model_3d_url,
            format: detect3DModelFormat(memoryData.model_3d_url),
          };

          setGenerated3DModel(model);
          updateGenerated3DModelDisplayUrl(memoryData.model_3d_url);
          setModelReady(true);
          setUploadError('');
        }
      } catch (error) {
        logger.error('Error during 3D polling:', error);
      }
    }, 5000); // Poll every 5 seconds

    modelPollRef.current = interval;
  };

  const stopPolling3D = () => {
    if (modelPollRef.current) {
      clearInterval(modelPollRef.current);
      modelPollRef.current = null;
    }
  };

  // Handle pre-filled data from Home page and refresh the memory from DB once
  useEffect(() => {
    const initFromState = async () => {
      const state = location.state;
      if (!state) return;
      const { editMode: isEditMode, memoryData } = state;
      if (!isEditMode || !memoryData?.id) return;

      setEditMode(isEditMode);
      setExistingMemory(memoryData);

      // Pre-fill the form with existing memory data
      setMemoryTitle(memoryData.title || '');
      setMemoryDescription(memoryData.description || '');
      setSavedMemory(memoryData);

      // If there's a photo URL, create a mock file object for display
      if (memoryData.photo_url) {
        const mockFile = {
          name: 'existing-photo.jpg',
          size: 1024000, // Mock size
          type: 'image/jpeg',
        };
        setSelectedFile(mockFile);
      }

      // Fetch a fresh copy from DB to avoid stale status/URL
      try {
        const latest = await checkMemoryStatus(memoryData.id);
        if (latest?.figurine_url) {
          const imageObj = {
            url: latest.figurine_url,
            alt: `Generated figurine for ${latest.title || memoryData.title}`,
          };
          setGeneratedImage(imageObj);
          updateGeneratedImageDisplayUrl(latest.figurine_url, {
            transform: { width: 1200, quality: 80, resize: 'contain' },
          });
          setSavedMemory((prev) =>
            prev
              ? {
                  ...prev,
                  figurine_url: latest.figurine_url,
                  figurine_urls: latest.figurine_urls || prev.figurine_urls,
                }
              : prev
          );
          setExistingMemory((prev) =>
            prev
              ? {
                  ...prev,
                  figurine_url: latest.figurine_url,
                  figurine_urls: latest.figurine_urls || prev.figurine_urls,
                }
              : prev
          );
          setImageRevealReady(true);
        }

        // If a 3D model is already available, initialize the viewer
        if (latest?.model_3d_url) {
          const model = {
            modelUrl: latest.model_3d_url,
            format: detect3DModelFormat(latest.model_3d_url),
          };
          setGenerated3DModel(model);
          updateGenerated3DModelDisplayUrl(latest.model_3d_url);
          setIsGenerating3D(false);
          setModelRevealReady(true);
        }

        if (
          latest?.status === 'processing' ||
          latest?.status === 'processing_cartoon' ||
          latest?.status === 'processing-cartoon'
        ) {
          setIsGenerating(true);
          startPollingForFigurine(memoryData.id);
        } else if (latest?.status === 'processing_3d' || latest?.status === 'processing-3d') {
          setIsGenerating3D(true);
          startPollingFor3DModel(memoryData.id);
        }
      } catch (e) {
        logger.error('Failed to refresh memory status:', e);
        // Fall back to state-based handling
        if (
          memoryData.status === 'processing' ||
          memoryData.status === 'processing_cartoon' ||
          memoryData.status === 'processing-cartoon'
        ) {
          setIsGenerating(true);
          startPollingForFigurine(memoryData.id);
        } else if (memoryData.figurine_url) {
          const imageObj = {
            url: memoryData.figurine_url,
            alt: `Generated figurine for ${memoryData.title}`,
          };
          setGeneratedImage(imageObj);
          updateGeneratedImageDisplayUrl(memoryData.figurine_url, {
            transform: { width: 1200, quality: 80, resize: 'contain' },
          });
          setSavedMemory((prev) =>
            prev
              ? {
                  ...prev,
                  figurine_urls: memoryData.figurine_urls || prev.figurine_urls,
                }
              : prev
          );
          setExistingMemory((prev) =>
            prev
              ? {
                  ...prev,
                  figurine_urls: memoryData.figurine_urls || prev.figurine_urls,
                }
              : prev
          );
          setImageRevealReady(true);
        }

        if (memoryData.model_3d_url) {
          const model = {
            modelUrl: memoryData.model_3d_url,
            format: detect3DModelFormat(memoryData.model_3d_url),
          };
          setGenerated3DModel(model);
          updateGenerated3DModelDisplayUrl(memoryData.model_3d_url);
          setIsGenerating3D(false);
          setModelRevealReady(true);
        } else if (memoryData.status === 'processing_3d' || memoryData.status === 'processing-3d') {
          setIsGenerating3D(true);
          startPollingFor3DModel(memoryData.id);
        }
      }
    };

    initFromState();
  }, [location.state]);

  // Calculate viewer dimensions based on viewport size
  const calculateViewerDimensions = () => {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Set height to 75% of viewport height, with a maximum of 600px
    const height = Math.min(viewportHeight * 0.75, 600);

    // Set width to maintain aspect ratio, but not exceed 80% of viewport width
    const maxWidth = viewportWidth * 0.8;
    const width = Math.min(height, maxWidth); // Keep it square or smaller than max width

    return { width: Math.round(width), height: Math.round(height) };
  };

  // Update viewer dimensions on mount and window resize
  useEffect(() => {
    const updateDimensions = () => {
      setViewerDimensions(calculateViewerDimensions());
    };

    // Set initial dimensions
    updateDimensions();

    // Add event listener for window resize
    window.addEventListener('resize', updateDimensions);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Fetch product price from database
  useEffect(() => {
    const fetchProductPrice = async () => {
      try {
        const result = await getProductPrice('Tiniego Figurine');
        if (result.success) {
          setProductPrice(result.price);
        } else {
          logger.error('Failed to fetch product price:', result.error);
          // Keep default fallback price
        }
      } catch (e) {
        logger.error('Failed to fetch product price:', e);
        // Keep default fallback price
      }
    };

    fetchProductPrice();
  }, []);

  // Fetch remaining figurine generations for the day
  useEffect(() => {
    const fetchRemaining = async () => {
      try {
        if (!user?.id) {
          setRemainingGenerations(null);
          setDailyLimit(null);
          return;
        }
        const res = await getRemainingFigurineGenerations(user.id);
        if (res.success) {
          setRemainingGenerations(res.remaining);
          setDailyLimit(res.dailyLimit);
        } else {
          setRemainingGenerations(null);
          setDailyLimit(null);
        }
      } catch (e) {
        logger.error('Failed to fetch remaining generations:', e);
      }
    };

    fetchRemaining();
  }, [user?.id]);

  // Populate figurineImages array when we have figurine_urls data
  useEffect(() => {
    const populateFigurineImages = async () => {
      const memory = savedMemory || existingMemory;
      if (!memory?.figurine_urls || memory.figurine_urls.length === 0) {
        setFigurineImages([]);
        setCurrentImageIndex(0);
        return;
      }

      try {
        const imagePromises = memory.figurine_urls.map(async (url, index) => {
          const displayUrl = await getDisplayUrl(url, {
            transform: { width: 1200, quality: 80, resize: 'contain' },
          });
          return {
            url,
            displayUrl,
            alt: `Generated figurine ${index + 1} for ${memory.title || memoryTitle}`,
          };
        });

        const images = await Promise.all(imagePromises);
        setFigurineImages(images);
        
        // Set current index to the last image (most recent)
        setCurrentImageIndex(images.length - 1);
      } catch (error) {
        logger.error('Error populating figurine images:', error);
        setFigurineImages([]);
        setCurrentImageIndex(0);
      }
    };

    populateFigurineImages();
  }, [savedMemory?.figurine_urls, existingMemory?.figurine_urls, memoryTitle]);

  // Cleanup polling intervals and timeouts on component unmount
  useEffect(() => {
    return () => {
      if (figurinePollRef.current) {
        clearInterval(figurinePollRef.current);
      }
      if (modelPollRef.current) {
        clearInterval(modelPollRef.current);
      }
      if (titleUpdateTimeoutRef.current) {
        clearTimeout(titleUpdateTimeoutRef.current);
      }
      if (figurineUrlUpdateTimeoutRef.current) {
        clearTimeout(figurineUrlUpdateTimeoutRef.current);
      }
    };
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    logger.debug('File selected:', file);
    if (file) {
      setSelectedFile(file);
      logger.debug('Selected file set:', file.name, file.type, file.size);
    }
  };

  // Debounced function to update memory title in database
  const updateMemoryTitleInDB = async (newTitle) => {
    const memoryId = savedMemory?.id || existingMemory?.id;
    if (!memoryId || !user) {
      return;
    }

    try {
      await updateMemory(memoryId, { title: newTitle });
      logger.debug('Memory title updated in database:', newTitle);

      // Update local state to keep it in sync
      if (savedMemory) {
        setSavedMemory((prev) => ({ ...prev, title: newTitle }));
      }
      if (existingMemory) {
        setExistingMemory((prev) => ({ ...prev, title: newTitle }));
      }
    } catch (error) {
      logger.error('Error updating memory title:', error);
    }
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setMemoryTitle(newTitle);

    // Clear existing timeout
    if (titleUpdateTimeoutRef.current) {
      clearTimeout(titleUpdateTimeoutRef.current);
    }

    // Set new timeout for debounced update
    titleUpdateTimeoutRef.current = setTimeout(() => {
      updateMemoryTitleInDB(newTitle);
    }, 1000); // Update after 1 second of no typing
  };

  const handleCreateFigurine = async (e) => {
    e.preventDefault();
    if (!selectedFile || !memoryTitle.trim()) {
      showError('📸 Please select a photo and give your tiniego a name to get started!');
      return;
    }

    if (!user) {
      showError('🔐 Please log in to create your tiniego memories!');
      navigate('/auth');
      return;
    }

    setIsGenerating(true);
    setUploadError('');

    try {
      // Create new memory record
      const uploadResult = await uploadPhoto(selectedFile, user.id);

      if (!uploadResult.success) {
        setUploadError(`Failed to upload photo: ${uploadResult.error}`);
        setIsGenerating(false);
        return;
      }

      const photoFilePath =
        uploadResult.filePath || extractFilePathFromUrl(uploadResult.url) || uploadResult.url;

      const memoryData = {
        user_id: user.id,
        title: memoryTitle,
        description: memoryDescription,
        photo_url: photoFilePath,
        status: 'uploaded',
        price: productPrice,
      };

      const savedMemoryData = await createMemory(memoryData);
      setSavedMemory(savedMemoryData);

      logger.debug('Memory saved:', savedMemoryData);

      // Generate figurine using real AI service
      logger.debug('Generating figurine:', {
        file: selectedFile.name,
        title: memoryTitle,
        description: memoryDescription,
        photoUrl: uploadResult.url,
        memoryId: savedMemoryData.id,
      });

      try {
        const figurineResult = await generateFigurine(
          photoFilePath,
          savedMemoryData.id,
          user.id,
          memoryTitle,
          memoryDescription
        );

        if (figurineResult.success) {
          const generatedImage = {
            url: figurineResult.figurine_url,
            alt: `Generated figurine for ${memoryTitle}`,
          };

          logger.debug('Setting generated image with URL:', figurineResult.figurine_url);
          logger.debug('Original photo URL was:', uploadResult.url);
          logger.debug('Generated image object:', generatedImage);

          setGeneratedImage(generatedImage);
          await updateGeneratedImageDisplayUrl(figurineResult.figurine_url, {
            transform: { width: 1200, quality: 80, resize: 'contain' },
          });
          logger.debug('Figurine generated successfully:', figurineResult.figurine_url);
          // Signal that image is ready; progress bar will handle reveal
          setImageReady(true);

          // Refresh memory to get updated figurine_urls for carousel
          try {
            const refreshed = await checkMemoryFigurineStatus(savedMemoryData.id);
            setSavedMemory((prev) =>
              prev
                ? {
                    ...prev,
                    figurine_url: refreshed.figurine_url || prev.figurine_url,
                    figurine_urls: refreshed.figurine_urls || prev.figurine_urls,
                  }
                : prev
            );
            setExistingMemory((prev) =>
              prev
                ? {
                    ...prev,
                    figurine_url: refreshed.figurine_url || prev.figurine_url,
                    figurine_urls: refreshed.figurine_urls || prev.figurine_urls,
                  }
                : prev
            );
          } catch (e) {
            logger.error('Failed to refresh figurine URLs after create:', e);
          }
        } else {
          // If generation is in progress, start polling
          logger.debug('Figurine generation in progress, starting polling...');
          startPollingForFigurine(savedMemoryData.id);
        }
      } catch (error) {
        logger.error('Error generating figurine:', error);

        // Check if it's a timeout or network error - if so, start polling
        if (
          error.name === 'AbortError' ||
          error.message.includes('timeout') ||
          error.message.includes('network')
        ) {
          logger.debug(
            'Request timed out or failed, but generation may still be in progress. Starting polling...'
          );
          startPollingForFigurine(savedMemoryData.id);
        } else {
          // For other errors, show error message
          setUploadError(`Failed to generate figurine: ${error.message}`);
          setIsGenerating(false);
        }
      }
    } catch (error) {
      logger.error('Error saving memory:', error);
      setUploadError(`Failed to save memory: ${error.message}`);
      setIsGenerating(false);
    }
  };

  const handleUpdateFigurine = async (e) => {
    e.preventDefault();
    if (!memoryTitle.trim()) {
      showError('✏️ Please give your tiniego a name!');
      return;
    }

    if (!user) {
      showError('🔐 Please log in to update your tiniego!');
      navigate('/auth');
      return;
    }

    if (!existingMemory) {
      showError('❌ No tiniego found to update. Please create one first!');
      return;
    }

    // Stop any existing polling
    stopPollingFigurine();

    setIsGenerating(true);
    setUploadError('');

    try {
      let uploadResult;

      // In edit mode, only upload new photo if a new file was selected
      if (selectedFile && selectedFile.name !== 'existing-photo.jpg') {
        uploadResult = await uploadPhoto(selectedFile, user.id);

        if (!uploadResult.success) {
          setUploadError(`Failed to upload photo: ${uploadResult.error}`);
          setIsGenerating(false);
          return;
        }
      } else {
        // Use existing photo URL (may be already a path)
        uploadResult = { success: true, url: existingMemory.photo_url };
      }

      // Update existing memory record
      const photoFilePath =
        uploadResult.filePath || extractFilePathFromUrl(uploadResult.url) || uploadResult.url;

      const memoryData = {
        title: memoryTitle,
        description: memoryDescription,
        photo_url: photoFilePath,
        status: 'uploaded',
      };

      const savedMemoryData = await updateMemory(existingMemory.id, memoryData);
      setSavedMemory(savedMemoryData);

      logger.debug('Memory updated:', savedMemoryData);

      // Regenerate figurine using real AI service
      logger.debug('Regenerating figurine:', {
        file: selectedFile?.name || 'existing-photo',
        title: memoryTitle,
        description: memoryDescription,
        photoUrl: uploadResult.url,
        memoryId: savedMemoryData.id,
      });

      try {
        const figurineResult = await generateFigurine(
          photoFilePath,
          savedMemoryData.id,
          user.id,
          memoryTitle,
          memoryDescription
        );

        if (figurineResult.success) {
          // If we get the figurine URL immediately, use it
          const generatedImage = {
            url: figurineResult.figurine_url,
            alt: `Generated figurine for ${memoryTitle}`,
          };

          setGeneratedImage(generatedImage);
          updateGeneratedImageDisplayUrl(figurineResult.figurine_url);
          logger.debug('Figurine regenerated successfully:', figurineResult.figurine_url);
          setImageReady(true);

          // Refresh memory to get updated figurine_urls for carousel
          try {
            const refreshed = await checkMemoryFigurineStatus(savedMemoryData.id);
            setSavedMemory((prev) =>
              prev
                ? {
                    ...prev,
                    figurine_url: refreshed.figurine_url || prev.figurine_url,
                    figurine_urls: refreshed.figurine_urls || prev.figurine_urls,
                  }
                : prev
            );
            setExistingMemory((prev) =>
              prev
                ? {
                    ...prev,
                    figurine_url: refreshed.figurine_url || prev.figurine_url,
                    figurine_urls: refreshed.figurine_urls || prev.figurine_urls,
                  }
                : prev
            );
          } catch (e) {
            logger.error('Failed to refresh figurine URLs after regenerate:', e);
          }
        } else {
          // If generation is in progress, start polling
          logger.debug('Figurine generation in progress, starting polling...');
          startPollingForFigurine(savedMemoryData.id);
        }
      } catch (error) {
        logger.error('Error regenerating figurine:', error);

        // Check if it's a timeout or network error - if so, start polling
        if (
          error.name === 'AbortError' ||
          error.message.includes('timeout') ||
          error.message.includes('network')
        ) {
          logger.debug(
            'Request timed out or failed, but generation may still be in progress. Starting polling...'
          );
          startPollingForFigurine(savedMemoryData.id);
        } else {
          // For other errors, show error message
          setUploadError(`Failed to regenerate figurine: ${error.message}`);
          setIsGenerating(false);
        }
      }
    } catch (error) {
      logger.error('Error updating memory:', error);
      setUploadError(`Failed to update memory: ${error.message}`);
      setIsGenerating(false);
    }
  };

  const handleGenerate3DModel = async () => {
    if (!generatedImage) {
      showError('🖼️ Please generate a 2D tiniego first before creating the 3D model!');
      return;
    }

    if (!savedMemory) {
      showError('💾 No tiniego found. Please create and save your tiniego first!');
      return;
    }

    if (!user) {
      showError('🔐 Please log in to generate 3D tiniego models!');
      navigate('/auth');
      return;
    }

    setHasClickedGenerate3D(true);
    setIsGenerating3D(true);
    setUploadError('');

    try {
      const result = await generate3DModelFromApi(user?.id, savedMemory.id, true);

      if (result.success) {
        // Use storage path directly if provided by API; otherwise infer from URL
        const storagePath =
          result.stl_storage_path || extractFilePathFromUrl(result.stl_url) || result.stl_url;

        const inferredFormat = detect3DModelFormat(storagePath);
        const model = {
          modelUrl: storagePath,
          format: inferredFormat,
        };
        setGenerated3DModel(model);
        updateGenerated3DModelDisplayUrl(storagePath);

        // Persist storage path to memory and mark as completed
        await updateMemory3DModel(savedMemory.id, storagePath);
        await updateMemoryStatus(savedMemory.id, 'completed');

        setModelReady(true);
      } else {
        // If generation is in progress, start polling
        console.log('3D model generation in progress, starting polling...');
        startPollingFor3DModel(savedMemory.id);
      }
    } catch (error) {
      logger.error('Generate 3D error:', error);

      // Check if it's a timeout or network error - if so, start polling
      if (
        error.name === 'AbortError' ||
        error.message.includes('timeout') ||
        error.message.includes('network')
      ) {
        logger.debug(
          'Request timed out or failed, but 3D generation may still be in progress. Starting polling...'
        );
        startPollingFor3DModel(savedMemory.id);
      } else {
        // For other errors, show error message
        setUploadError(`Failed to generate 3D model: ${error.message}`);
        setIsGenerating3D(false);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    logger.debug('File dropped:', file);
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      logger.debug('Dropped file set:', file.name, file.type, file.size);
    } else {
      logger.debug('Invalid file type dropped:', file?.type);
    }
  };

  const handleDeleteMemory = () => {
    if (!savedMemory && !existingMemory) {
      showError('❌ No tiniego found to delete!');
      return;
    }

    const memoryToDelete = savedMemory || existingMemory;

    showDeleteConfirm(
      'Are you sure you want to delete this tiniego? This action cannot be undone.',
      async () => {
        try {
          await deleteMemory(memoryToDelete.id);
          navigate('/home');
        } catch (error) {
          console.error('Error deleting memory:', error);
          showError(`❌ Could not delete tiniego: ${error.message}. Please try again.`);
        }
      }
    );
  };

  const handleAddToCart = async () => {
    if (!user) {
      showSuccess('🔐 Please log in to add tiniego to your cart!', {
        text: 'OK',
        onClick: () => navigate('/auth'),
      });
      return;
    }

    if (!savedMemory && !existingMemory) {
      showSuccess('❌ No tiniego found to add to cart!', {
        text: 'OK',
        onClick: () => console.log('OK clicked'),
      });
      return;
    }

    const memoryToAdd = savedMemory || existingMemory;

    try {
      // Use the product price from database
      const price = productPrice;

      await addToCart(memoryToAdd.id, 1, price, user.id);
      showSuccess('🎉 Awesome! Your tiniego has been added to your cart!', {
        text: 'Go to Cart',
        onClick: () => navigate('/cart'),
        style: { backgroundColor: '#4CAF50', color: 'white' },
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      showSuccess('❌ Could not add tiniego to cart. Please try again!', {
        text: 'OK',
        onClick: () => console.log('OK clicked'),
      });
    }
  };

  return (
    <div className="create-memories-page">
      <Navbar />

      <div className="content">
        <div className="create-memories-container">
          <div className="page-header-section">
            <div className="section-content">
              <h1>Let's create your tiniego!</h1>
            </div>
          </div>

          <div className="upload-section">
            <div className="section-content">
              <h2 className="section-title">First, upload a photo and give it a name</h2>

              <div
                className={`file-upload-area ${selectedFile ? 'has-file' : ''}`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => {
                  if (!selectedFile) {
                    fileInputRef.current && fileInputRef.current.click();
                  }
                }}
                style={{ cursor: selectedFile ? 'default' : 'pointer' }}
              >
                {selectedFile ? (
                  <div className="file-preview">
                    {(() => {
                      const src =
                        selectedFile.name === 'existing-photo.jpg'
                          ? existingPhotoDisplayUrl
                          : previewUrl;
                      return src ? (
                        <img
                          src={src}
                          alt="Preview"
                          className="image-preview"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : null;
                    })()}
                    <div
                      className={`file-overlay ${savedMemory?.id || existingMemory?.id ? 'disabled' : ''}`}
                    >
                      <div className="file-info">
                        <p className="file-name">{selectedFile.name}</p>
                        <p className="file-size">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      {!(savedMemory?.id || existingMemory?.id) && (
                        <button
                          type="button"
                          className="remove-file-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (previewUrl) {
                              URL.revokeObjectURL(previewUrl);
                              setPreviewUrl(null);
                            }
                            setSelectedFile(null);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <span className="material-symbols-outlined upload-icon">image</span>
                    <h2>Upload a photo</h2>
                    <input
                      id="file-input"
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                    />
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginTop: '1rem' }}>
                <input
                  type="text"
                  id="memory-title"
                  value={memoryTitle}
                  onChange={handleTitleChange}
                  placeholder="e.g. Ana in Paris"
                  required
                />
              </div>

              <div
                className="upload-tips"
                style={{ fontSize: '0.85rem', color: '#666', marginTop: '1rem' }}
              >
                💡 Tips for Best Results: Use high-quality photos, ensure good lighting and clear
                focus, include the main subject clearly in the frame, front-facing portraits work
                best for figurines.
              </div>
            </div>
          </div>

          {!imageRevealReady && (
            <div className="generate-section">
              <div className="section-content">
                <h2 className="section-title">Next, generate your tiniego</h2>
                <div className="form-actions">
                  <Button
                    variant="primary"
                    className="submit-btn"
                    onClick={editMode ? handleUpdateFigurine : handleCreateFigurine}
                    disabled={
                      !memoryTitle.trim() ||
                      isGenerating ||
                      (typeof remainingGenerations === 'number' && remainingGenerations <= 0)
                    }
                    isLoading={isGenerating}
                    fullWidth
                  >
                    {isGenerating ? 'Sketching tiniego..' : 'Generate your tiniego'}
                  </Button>
                  {isGenerating && (
                    <div className="progress-bar" aria-label="generation-progress">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${generationProgress}%` }}
                      ></div>
                    </div>
                  )}
                  {isGenerating && (
                    <p className="generation-tip">
                      Please wait, this may take a few minutes to sketch.
                    </p>
                  )}
                  {typeof remainingGenerations === 'number' && (
                    <>
                      <p className="generation-tip" style={{ marginTop: '0.5rem' }}>
                        You have {remainingGenerations} generations remaining.<br></br>
                        Number of generations are reset daily.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {generatedImage && imageRevealReady && (
            <div className="generate-section">
              <div className="section-content">
                <h2 className="section-title">Next, generate your tiniego</h2>
                <div className="generated-result">
                  <div className="result-container">
                    <div className="generated-image has-image">
                      <img
                        src={getCurrentFigurineImageDisplayUrl() || getCurrentFigurineImage()?.url}
                        alt={getCurrentFigurineImage()?.alt || generatedImage?.alt}
                        className="figurine-preview"
                        loading="lazy"
                        decoding="async"
                        onTouchStart={handleFigurineTouchStart}
                        onTouchMove={handleFigurineTouchMove}
                        onTouchEnd={handleFigurineTouchEnd}
                        onError={(e) => {
                          console.error('Failed to load figurine image:', getCurrentFigurineImageDisplayUrl() || getCurrentFigurineImage()?.url);
                          e.currentTarget.src = '/placeholder-image.png';
                        }}
                      />
                    </div>
                    {/* Carousel controls and dots - only show if more than 1 image */}
                    {figurineImages.length > 1 && (
                      <div className="carousel-controls">
                        <button
                          type="button"
                          className="carousel-nav-btn prev"
                          onClick={goToPrevImage}
                          aria-label="Previous image"
                        >
                          ‹
                        </button>
                        <div className="carousel-dots">
                          {figurineImages.map((_, index) => (
                            <button
                              key={index}
                              type="button"
                              className={`carousel-dot ${index === currentImageIndex ? 'active' : ''}`}
                              onClick={() => setCurrentImageIndex(index)}
                              aria-label={`View figurine image ${index + 1}`}
                            />
                          ))}
                        </div>
                        <button
                          type="button"
                          className="carousel-nav-btn next"
                          onClick={goToNextImage}
                          aria-label="Next image"
                        >
                          ›
                        </button>
                      </div>
                    )}
                    <div className="result-details">
                      {memoryDescription && <p>{memoryDescription}</p>}
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  {/* Only show regenerate button if status is not processing_3d or completed, and user hasn't clicked Generate 3D Model */}
                  {!hasClickedGenerate3D &&
                    ((savedMemory &&
                      savedMemory.status !== 'processing_3d' &&
                      savedMemory.status !== 'completed') ||
                      (existingMemory &&
                        existingMemory.status !== 'processing_3d' &&
                        existingMemory.status !== 'completed')) && (
                      <Button
                        variant="secondary"
                        className="regenerate-btn"
                        onClick={async () => {
                          // Immediately switch back to the generate section and show loading UI
                          setImageRevealReady(false);
                          setImageReady(false);
                          setGeneratedImage(null);
                          setGeneratedImageDisplayUrl(null);
                          setGenerated3DModel(null);
                          setGenerated3DModelDisplayUrl(null);
                          setUploadError('');
                          setHasClickedGenerate3D(false);
                          setFigurineImages([]);
                          setCurrentImageIndex(0);

                          // Stop any existing polling
                          stopPollingFigurine();

                          // If we have a saved memory, regenerate the figurine
                          if (savedMemory) {
                            setIsGenerating(true);

                            try {
                              const figurineResult = await generateFigurine(
                                savedMemory.photo_url,
                                savedMemory.id,
                                user.id,
                                memoryTitle,
                                memoryDescription
                              );

                              if (figurineResult.success) {
                                const generatedImage = {
                                  url: figurineResult.figurine_url,
                                  alt: `Generated figurine for ${memoryTitle}`,
                                };

                                setGeneratedImage(generatedImage);
                                await updateGeneratedImageDisplayUrl(figurineResult.figurine_url, {
                                  transform: { width: 1200, quality: 80, resize: 'contain' },
                                });
                                console.log(
                                  'Figurine regenerated successfully:',
                                  figurineResult.figurine_url
                                );
                                // Immediately mark image as ready and reveal it so UI updates without navigation
                                setImageReady(true);
                                setImageRevealReady(true);
                                setIsGenerating(false);

                                // Refresh memory to get updated figurine_urls for carousel
                                try {
                                  const refreshed = await checkMemoryFigurineStatus(savedMemory.id);
                                  setSavedMemory((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          figurine_url: refreshed.figurine_url || prev.figurine_url,
                                          figurine_urls: refreshed.figurine_urls || prev.figurine_urls,
                                        }
                                      : prev
                                  );
                                  setExistingMemory((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          figurine_url: refreshed.figurine_url || prev.figurine_url,
                                          figurine_urls: refreshed.figurine_urls || prev.figurine_urls,
                                        }
                                      : prev
                                  );
                                } catch (e) {
                                  console.error('Failed to refresh figurine URLs after inline regenerate:', e);
                                }
                              } else {
                                // If generation is in progress, start polling
                                console.log(
                                  'Figurine regeneration in progress, starting polling...'
                                );
                                startPollingForFigurine(savedMemory.id);
                              }
                            } catch (error) {
                              console.error('Error regenerating figurine:', error);

                              // Check if it's a timeout or network error - if so, start polling
                              if (
                                error.name === 'AbortError' ||
                                error.message.includes('timeout') ||
                                error.message.includes('network')
                              ) {
                                console.log(
                                  'Request timed out or failed, but generation may still be in progress. Starting polling...'
                                );
                                startPollingForFigurine(savedMemory.id);
                              } else {
                                // For other errors, show error message
                                setUploadError(`Failed to regenerate figurine: ${error.message}`);
                                setIsGenerating(false);
                              }
                            }
                          } else {
                            // Reset form for new creation
                            setSelectedFile(null);
                            setMemoryTitle('');
                            setMemoryDescription('');
                            setSavedMemory(null);
                          }
                        }}
                      >
                        Regenerate
                      </Button>
                    )}
                </div>

                {uploadError && (
                  <div
                    className="error-message"
                    style={{
                      color: '#f44336',
                      backgroundColor: '#FFEBEE',
                      padding: '12px',
                      borderRadius: '4px',
                      marginTop: '1rem',
                      border: '1px solid #f44336',
                    }}
                  >
                    {uploadError}
                  </div>
                )}
              </div>
            </div>
          )}

          {generatedImage && (
            <div className="generate-3d-section">
              <div className="section-content">
                <h2 className="section-title">Finally, let's make it 3D!</h2>
                {(!generated3DModel || (generated3DModel && !modelRevealReady)) && (
                  <div className="form-actions">
                    <Button
                      variant="primary"
                      className="generate-3d-btn"
                      onClick={handleGenerate3DModel}
                      disabled={!memoryTitle.trim() || isGenerating3D || isGenerating}
                      isLoading={isGenerating3D}
                    >
                      {isGenerating3D ? 'Assembling tiniego..' : 'Generate 3D Model'}
                    </Button>
                    {isGenerating3D && (
                      <div className="progress-bar" aria-label="generation-3d-progress">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${generation3DProgress}%` }}
                        ></div>
                      </div>
                    )}
                    {isGenerating3D && (
                      <p className="generation-tip">
                        Please wait, this may take a few minutes to assemble
                      </p>
                    )}
                  </div>
                )}

                {generated3DModel && modelRevealReady && (
                  <div className="generated-3d-result">
                    <div className="result-container-3d">
                      <div className="generated-3d-viewer">
                        <STLViewer
                          modelUrl={generated3DModelDisplayUrl || generated3DModel.modelUrl}
                          format={generated3DModel.format}
                          width={viewerDimensions.width}
                          height={viewerDimensions.height}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {generated3DModel && modelRevealReady && (
                  <div className="form-actions" style={{ marginTop: '1rem' }}>
                    <Button variant="primary" className="order-print-btn" onClick={handleAddToCart}>
                      Add to Cart
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Delete Memory Section - only show after Step 2 has completed (generated image exists) */}
          {generatedImage && (savedMemory || existingMemory) && (
            <div className="delete-memory-section">
              <div className="section-content">
                <div className="form-actions">
                  <Button
                    variant="danger"
                    className="delete-memory-btn"
                    onClick={handleDeleteMemory}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Create;
